import { AnimatePresence, motion } from 'framer-motion'
import { Route, X } from 'lucide-react'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/utils/cn'

interface PathResultBannerProps {
  visible: boolean
  /** Hop count of the found path (edges between source and target). */
  pathLength: number
  /** How many path entities were not in the view and are shown provisionally. */
  addedCount: number
  /** Commit the provisionally-added entities to the investigation. */
  onKeep: () => void
  /** Drop the provisional entities and the highlight. Also bound to Esc. */
  onDismiss: () => void
}

/**
 * Bottom-left result pill for the ring-menu Find Path tool — the counterpart
 * of ConnectionTargetBanner once a path came back from the backend. Shows how
 * far the target is and, when the path pulled entities from outside the
 * current view, asks whether to keep them in the graph. Both actions clear
 * the banner and the path highlight.
 */
export function PathResultBanner({
  visible,
  pathLength,
  addedCount,
  onKeep,
  onDismiss,
}: PathResultBannerProps) {
  const { t } = useTranslation()

  useEffect(() => {
    if (!visible) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      // Dismissing destroys the provisional result — don't hijack an Escape
      // meant for a focused input (notes composer, toolbar search).
      const el = document.activeElement
      if (el?.tagName === 'INPUT' || el?.tagName === 'TEXTAREA') return
      e.preventDefault()
      onDismiss()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [visible, onDismiss])

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="path-result-banner"
          role="status"
          aria-live="polite"
          initial={{ opacity: 0, y: 16, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 10, scale: 0.97 }}
          transition={{ type: 'spring', stiffness: 360, damping: 28, mass: 0.85 }}
          className={cn(
            'pointer-events-auto absolute bottom-6 left-6 z-30',
            'max-md:right-3 max-md:bottom-12 max-md:left-3 max-md:flex-wrap max-md:gap-x-2 max-md:gap-y-1.5',
            'flex items-center gap-3 rounded-xl border px-3 py-2',
            'border-border bg-accent text-accent-foreground',
            'shadow-[0_18px_48px_-16px_hsl(var(--foreground)/0.32),inset_0_1px_0_hsl(0_0%_100%/0.10)]'
          )}
        >
          <Route className="h-4 w-4 flex-shrink-0 text-primary" />

          {/* Distance — how far the destination is, in hops */}
          <div className="flex min-w-0 flex-col leading-tight">
            <span className="text-[9px] font-medium uppercase tracking-[0.18em] text-muted-foreground/80">
              {t('investigations.graph.pathTool.result.distanceLabel')}
            </span>
            <span className="text-[12px] font-medium text-foreground">
              {t('investigations.graph.pathTool.result.distance', { count: pathLength })}
            </span>
          </div>

          {addedCount > 0 && (
            <>
              <span className="h-6 w-px flex-shrink-0 bg-border" aria-hidden="true" />
              <span className="max-w-[220px] text-[11px] text-muted-foreground">
                {t('investigations.graph.pathTool.result.added', { count: addedCount })}
              </span>
              <button
                type="button"
                onClick={onKeep}
                className={cn(
                  'flex-shrink-0 rounded-md px-2 py-1 text-[11px] font-medium max-md:px-3 max-md:py-2',
                  'bg-primary text-primary-foreground transition-colors hover:bg-primary/90'
                )}
              >
                {t('investigations.graph.pathTool.result.keep')}
              </button>
            </>
          )}

          {/* Dismiss — mirrors ConnectionTargetBanner's esc affordance */}
          <button
            type="button"
            onClick={onDismiss}
            className={cn(
              'ml-1 inline-flex items-center gap-1.5 rounded-md border px-1.5 py-1 max-md:px-2.5 max-md:py-2',
              'border-border/40 bg-muted/30 text-muted-foreground',
              'transition-colors hover:border-border/60 hover:bg-muted/60 hover:text-foreground'
            )}
            title={t('investigations.graph.pathTool.result.dismiss')}
          >
            <X className="h-3 w-3" />
            <span className="font-mono text-[9px] uppercase tracking-[0.14em]">esc</span>
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
